import vtkInteractorStyleManipulator from '@kitware/vtk.js/Interaction/Style/InteractorStyleManipulator';
import macro from '@kitware/vtk.js/macro';
import { IRenderWindowInteractorEvent } from '@kitware/vtk.js/Rendering/Core/RenderWindowInteractor';
import vtkOpenGLRenderWindow from '@kitware/vtk.js/Rendering/OpenGL/RenderWindow';
import vtkAbstractWidget from '@kitware/vtk.js/Widgets/Core/AbstractWidget';

import { Model } from '../model';
import { PaintWidgetState } from './state';

export function shouldIgnoreEvent(e: IRenderWindowInteractorEvent) {
  return e.altKey || e.controlKey || e.shiftKey;
}

export default function widgetBehavior(publicAPI: vtkInteractorStyleManipulator & vtkAbstractWidget, model: Model<PaintWidgetState>) {
  model.classHierarchy.push('vtkPaintWidgetProp');

  // support setting per-view widget manipulators
  macro.setGet(publicAPI, model, ['manipulator']);

  let isPainting = false;

  /**
   * Starts painting
   */
  publicAPI.handleLeftButtonPress = (eventData: IRenderWindowInteractorEvent) => {
    if (!model.manipulator || shouldIgnoreEvent(eventData)) {
      return macro.VOID;
    }

    const worldCoords = model.manipulator.handleEvent(
      eventData,
      model._apiSpecificRenderWindow as unknown as vtkOpenGLRenderWindow,
    ).worldCoords;

    if (!worldCoords) {
      return macro.VOID;
    }

    const brush = model.widgetState.getBrush();
    brush.setOrigin(worldCoords);

    isPainting = true;
    publicAPI.invokeStartInteractionEvent();
    return macro.EVENT_ABORT;
  };

  /**
   * Paints
   */
  publicAPI.handleMouseMove = (eventData: IRenderWindowInteractorEvent) => {
    if (shouldIgnoreEvent(eventData) || !model.manipulator) {
      return macro.VOID;
    }

    const worldCoords = model.manipulator.handleEvent(
      eventData,
      model._apiSpecificRenderWindow as unknown as vtkOpenGLRenderWindow,
    ).worldCoords;

    if (!worldCoords) {
      return macro.VOID;
    }

    const brush = model.widgetState.getBrush();
    brush.setOrigin(worldCoords);

    if (isPainting) {
      publicAPI.invokeInteractionEvent();
    }
    return macro.EVENT_ABORT;
  };

  /**
   * Finishes paint
   */
  publicAPI.handleLeftButtonRelease = (eventData: IRenderWindowInteractorEvent) => {
    if (!isPainting || shouldIgnoreEvent(eventData)) {
      return macro.VOID;
    }

    isPainting = false;
    publicAPI.invokeEndInteractionEvent();
    return macro.EVENT_ABORT;
  };

  publicAPI.handleMouseWheel = () => {
    if (isPainting) {
      return macro.EVENT_ABORT;
    }
    return macro.VOID;
  };

  publicAPI.grabFocus = () => {
    if (!model.hasFocus) {
      model.hasFocus = true;
      model._interactor.requestAnimation(publicAPI);
    }
  };

  publicAPI.loseFocus = () => {
    if (model.hasFocus) {
      model._interactor.cancelAnimation(publicAPI);
    }
    model.hasFocus = false;
    // model._widgetManager.enablePicking();
    // model._interactor.render();
  };
}
